import { telegram } from './telegram';
import type {
  ApiErrorBody,
  DetailResponse,
  Drama,
  FavoriteView,
  HistoryView,
  ListResponse,
  PageResult,
  PlatformHealthEntry,
  PlatformItem,
  UpdateEntry,
  VideoResponse,
} from './types';

/**
 * Error tipe-safe untuk response non-2xx dari backend kita.
 * Menyimpan `code` (mis. `NOT_FOUND`) supaya UI dapat kasih copy yg pas.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody | { error?: undefined }) {
    const errBody = 'error' in body ? body.error : undefined;
    super(errBody?.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = errBody?.code ?? 'HTTP_ERROR';
    this.details = errBody?.details;
  }
}

/**
 * `apiFetch` — bungkus `fetch` supaya:
 *   - path relatif ke `/api/v1/...` (Vite proxy meneruskan ke backend)
 *   - lampirkan `Authorization: tma <initData>` bila di dalam Telegram
 *   - lempar `ApiError` untuk non-2xx, dengan body ter-decode
 *   - timeout via AbortController (default 20s)
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20_000);

  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (telegram.initData.length > 0) {
    headers.set('Authorization', `tma ${telegram.initData}`);
  }

  // Di dev SELALU relatif: lewat proxy Vite, jadi tidak kena CORS walau
  // VITE_BACKEND_URL diisi (di dev itu dipakai sebagai TARGET proxy).
  // Di prod build (tanpa proxy) barulah URL backend penuh dipakai —
  // ter-inline ke bundle saat `vite build`.
  const backend = import.meta.env.DEV
    ? ''
    : ((import.meta.env['VITE_BACKEND_URL'] as string | undefined) ?? '');
  const url = backend.length > 0 ? `${backend.replace(/\/$/, '')}/api/v1${path}` : `/api/v1${path}`;

  try {
    const res = await fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    });
    const text = await res.text();
    let body: unknown = null;
    if (text.length > 0) {
      try {
        body = JSON.parse(text);
      } catch {
        // biarkan body = null; kita bisa mendeteksi non-JSON
      }
    }
    if (!res.ok) {
      throw new ApiError(res.status, (body as ApiErrorBody) ?? {});
    }
    return body as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(504, {
        error: {
          code: 'TIMEOUT',
          message: 'Permintaan terlalu lama. Coba lagi.',
        },
      });
    }
    const msg = err instanceof Error ? err.message : String(err);
    throw new ApiError(0, {
      error: { code: 'NETWORK_ERROR', message: msg },
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve URL absolut untuk resource yang di-serve backend (mis. HLS
 * manifest, subtitle). Backend return path relatif `/api/v1/media/...`
 * — di dev, Vite proxy meneruskan. Di prod (frontend di Cloudflare,
 * backend di Railway) kita HARUS prefix dgn VITE_BACKEND_URL, kalau
 * tidak browser fetch relative ke origin Cloudflare (404).
 */
export function resolveMediaUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Sama seperti apiFetch: di dev biarkan relatif supaya lewat proxy Vite.
  const backend = import.meta.env.DEV
    ? ''
    : ((import.meta.env['VITE_BACKEND_URL'] as string | undefined) ?? '');
  if (backend.length === 0) return url;
  const base = backend.replace(/\/$/, '');
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
}

// ────────────────────────────────────────────────────────────────
// Endpoint methods (typed)
// ────────────────────────────────────────────────────────────────

export const api = {
  platforms: () => apiFetch<{ items: PlatformItem[] }>('/platforms'),

  /**
   * `limit` hanya berlaku untuk feed gabungan (`platform=all`) — feed itu
   * bisa berisi >2000 judul, jadi dipotong supaya beranda tetap ringan.
   */
  home: (platform: string, lang: string, limit?: number) =>
    apiFetch<ListResponse<Drama> & { total?: number; hasMore?: boolean }>(
      `/home?platform=${encodeURIComponent(platform)}&lang=${encodeURIComponent(lang)}` +
        (limit !== undefined ? `&limit=${limit}` : ''),
    ),

  /**
   * Feed per KATEGORI tontonan (populer / drama short / serial / anime /
   * donghua). Backend yang menggolongkan tiap judul — satu platform bisa
   * memuat lebih dari satu jenis.
   */
  categoryHome: (category: string, lang: string, limit?: number) =>
    apiFetch<ListResponse<Drama> & { total?: number; hasMore?: boolean }>(
      `/home?category=${encodeURIComponent(category)}&lang=${encodeURIComponent(lang)}` +
        (limit !== undefined ? `&limit=${limit}` : ''),
    ),

  banner: (platform: string, lang: string) =>
    apiFetch<ListResponse<Drama>>(
      `/banner?platform=${encodeURIComponent(platform)}&lang=${encodeURIComponent(lang)}`,
    ),

  discover: (platform: string, lang: string, page: number) =>
    apiFetch<PageResult<Drama>>(
      `/discover?platform=${encodeURIComponent(platform)}&lang=${encodeURIComponent(lang)}&page=${page}`,
    ),

  detail: (platform: string, id: string, lang: string) =>
    apiFetch<DetailResponse>(
      `/detail?platform=${encodeURIComponent(platform)}&id=${encodeURIComponent(id)}&lang=${encodeURIComponent(lang)}`,
    ),

  video: (platform: string, id: string, chapterId: string, lang: string) =>
    apiFetch<VideoResponse>(
      `/video?platform=${encodeURIComponent(platform)}&id=${encodeURIComponent(id)}&chapterId=${encodeURIComponent(chapterId)}&lang=${encodeURIComponent(lang)}`,
    ),

  search: (platform: string, q: string, lang: string, page: number) =>
    apiFetch<PageResult<Drama>>(
      `/search?platform=${encodeURIComponent(platform)}&q=${encodeURIComponent(q)}&lang=${encodeURIComponent(lang)}&page=${page}`,
    ),

  /**
   * Judul sorotan untuk carousel beranda. `verified: false` berarti daftar
   * terverifikasi belum siap di server dan ini kandidat mentah.
   */
  spotlight: (lang: string, limit = 6) =>
    apiFetch<{ items: UpdateEntry[]; verified: boolean }>(
      `/spotlight?lang=${encodeURIComponent(lang)}&limit=${limit}`,
    ),

  platformHealth: () =>
    apiFetch<{ items: PlatformHealthEntry[] }>('/platform-health'),

  // ─── Favorites (butuh login Telegram) ───
  favorites: () => apiFetch<{ items: FavoriteView[] }>('/favorites'),
  isFavorite: (platform: string, dramaId: string) =>
    apiFetch<{ favorite: boolean }>(
      `/favorites/${encodeURIComponent(platform)}/${encodeURIComponent(dramaId)}`,
    ),
  addFavorite: (body: {
    platform: string;
    dramaId: string;
    title: string;
    poster?: string;
    description?: string;
    episodeCount?: number;
  }) =>
    apiFetch<{ item: FavoriteView }>('/favorites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  removeFavorite: (platform: string, dramaId: string) =>
    apiFetch<{ removed: boolean }>(
      `/favorites/${encodeURIComponent(platform)}/${encodeURIComponent(dramaId)}`,
      { method: 'DELETE' },
    ),

  // ─── History (butuh login Telegram) ───
  history: () => apiFetch<{ items: HistoryView[] }>('/history'),
  recordHistory: (body: {
    platform: string;
    dramaId: string;
    episodeId: string;
    episodeNumber: number;
    dramaTitle: string;
    dramaPoster?: string;
    episodeTitle?: string;
    totalEpisodes?: number;
    positionSeconds: number;
    durationSeconds?: number;
    completed?: boolean;
  }) =>
    apiFetch<{ item: HistoryView }>('/history', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  updates: (
    opts: {
      platform?: string;
      category?: string;
      limit?: number;
      since?: number;
    } = {},
  ) => {
    const p = new URLSearchParams();
    if (opts.platform !== undefined) p.set('platform', opts.platform);
    if (opts.category !== undefined) p.set('category', opts.category);
    if (opts.limit !== undefined) p.set('limit', String(opts.limit));
    if (opts.since !== undefined) p.set('since', String(opts.since));
    const qs = p.toString();
    return apiFetch<{ items: UpdateEntry[] }>(
      `/updates${qs.length > 0 ? `?${qs}` : ''}`,
    );
  },
};
